import { useEffect } from "react";

const DEFAULT_SITE_URL = "https://fit-log.com";
const DEFAULT_IMAGE_PATH = "/images/fit-log-logo.png";
const SITE_NAME = "Fit-Log";

function upsertMeta(selector, attributes, content) {
  if (typeof document === "undefined") {
    return;
  }

  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    Object.entries(attributes).forEach(([key, value]) => {
      tag.setAttribute(key, value);
    });
    document.head.appendChild(tag);
  }

  tag.setAttribute("content", content);
}

function upsertCanonical(href) {
  if (typeof document === "undefined") {
    return;
  }

  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }

  link.setAttribute("href", href);
}

function syncAlternateLinks(siteUrl, alternateLocales = []) {
  if (typeof document === "undefined") {
    return;
  }

  document.head
    .querySelectorAll('link[rel="alternate"][data-seo-alternate="true"]')
    .forEach((node) => node.remove());

  alternateLocales.forEach(({ hrefLang, path }) => {
    if (!hrefLang || !path) {
      return;
    }

    const link = document.createElement("link");
    link.setAttribute("rel", "alternate");
    link.setAttribute("hreflang", hrefLang);
    link.setAttribute("href", new URL(path, `${siteUrl}/`).toString());
    link.setAttribute("data-seo-alternate", "true");
    document.head.appendChild(link);
  });
}

function upsertJsonLd(jsonLd) {
  if (typeof document === "undefined") {
    return;
  }

  const existing = document.getElementById("seo-json-ld");
  if (!jsonLd) {
    if (existing) {
      existing.remove();
    }
    return;
  }

  const script = existing || document.createElement("script");
  script.setAttribute("id", "seo-json-ld");
  script.setAttribute("type", "application/ld+json");
  script.textContent = JSON.stringify(jsonLd);
  if (!existing) {
    document.head.appendChild(script);
  }
}

export default function SeoHead({
  title,
  description,
  canonicalPath,
  noIndex = false,
  imagePath = DEFAULT_IMAGE_PATH,
  jsonLd = null,
  alternateLocales = [],
}) {
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    const siteUrl = (process.env.REACT_APP_SITE_URL || DEFAULT_SITE_URL).replace(/\/+$/, "");
    const path = canonicalPath || window.location.pathname;
    const canonicalUrl = new URL(path, `${siteUrl}/`).toString();
    const imageUrl = new URL(imagePath, `${siteUrl}/`).toString();
    const robots = noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large";

    document.title = title;

    upsertCanonical(canonicalUrl);
    upsertMeta('meta[name="description"]', { name: "description" }, description);
    upsertMeta('meta[name="robots"]', { name: "robots" }, robots);
    upsertMeta('meta[property="og:title"]', { property: "og:title" }, title);
    upsertMeta('meta[property="og:description"]', { property: "og:description" }, description);
    upsertMeta('meta[property="og:type"]', { property: "og:type" }, "website");
    upsertMeta('meta[property="og:url"]', { property: "og:url" }, canonicalUrl);
    upsertMeta('meta[property="og:image"]', { property: "og:image" }, imageUrl);
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name" }, SITE_NAME);
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card" }, "summary_large_image");
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title" }, title);
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description" }, description);
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image" }, imageUrl);
    syncAlternateLinks(siteUrl, alternateLocales);
    upsertJsonLd(jsonLd);
  }, [title, description, canonicalPath, noIndex, imagePath, jsonLd, alternateLocales]);

  return null;
}
